import dayjs from 'dayjs'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'

import { fetchScenarios } from '@/entities/schedules'
import { useSaveScenarioMutation } from '@/entities/schedules/apis/planningApi'
import { setToast } from '@/entities/toast'
import { Button, Modal } from '@/shared/components'
import { DatePicker } from '@/shared/components/DatePicker/DatePicker'
import { DialogComponentProps } from '@/shared/components/Modal/models/DialogStructure'
import { TextField } from '@/shared/components/TextField/TextField'
import { useAppDispatch } from '@/shared/store'
import { FormControlLabel, FormHelperText, MenuItem, styled, Switch, Typography } from '@mui/material'

export type AddScenarioDialogProps = DialogComponentProps & {
  onClose: () => void
}

type AddScenarioFormValues = {
  scenarioName: string
  isReusable: boolean
  dateOfOperation: dayjs.Dayjs
  dayOfWeek: number
}

export const AddScenarioDialog: React.FC<AddScenarioDialogProps> = ({ open, onClose }) => {
  const [saveScenario, { isLoading }] = useSaveScenarioMutation()
  const dispatch = useAppDispatch()

  const { control, watch, handleSubmit } = useForm<AddScenarioFormValues>({
    defaultValues: {
      scenarioName: '',
      isReusable: false,
      dateOfOperation: dayjs(),
      dayOfWeek: dayjs().day(),
    },
  })

  const isReusable = watch('isReusable')

  const onSubmit: SubmitHandler<AddScenarioFormValues> = async (data) => {
    try {
      const scenario = await saveScenario({
        dateOfOperation: data.isReusable ? null : data.dateOfOperation.format('DD/MM/YYYY'),
        isPopulatedFromBookings: false,
        name: data.scenarioName,
        weekday: data.isReusable ? data.dayOfWeek : data.dateOfOperation.day(),
      }).unwrap()

      // The following lines are not executed if the saveScenario mutation fails due to error thrown
      dispatch(fetchScenarios({ scenarioIds: [+scenario.id] })).then(() => {
        dispatch(setToast({
          open: true,
          severity: 'success',
          title: 'Scenario Created',
          content: `Scenario ${scenario.name} has been created successfully.`,
        }))
      })

      onClose()
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <Modal
      title={'Create Scenario'}
      onClose={onClose}
      open={open}
      transition="opacity"
      showBackground={true}
      disableClose={isLoading}
    >
      <StyledContent>
        <Typography variant="body1">Required fields are marked with an asterisk *</Typography>

        <StyledForm onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">
          <FormControlLabel
            control={
              <Controller
                name="isReusable"
                control={control}
                render={({ field }) => (
                  <Switch
                    {...field}
                    checked={field.value}
                  />
                )}
              />
            }
            label="Reusable"
          />
          <FormHelperText>Toggle on and select a day of week to create a weekly reusable sceanrio.</FormHelperText>
          <Controller
            name="scenarioName"
            control={control}
            rules={{ required: 'Scenario Name is required' }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Scenario Name"
                placeholder="Enter scenario name"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                margin="normal"
              />
            )}
          />
          <Controller
            name="dayOfWeek"
            control={control}
            rules={{ required: 'Day of Week is required' }}
            render={({ field }) => (
              <TextField
                sx={{ display: isReusable ? 'block' : 'none' }}
                required
                select
                label="Day of Week"
                fullWidth
                {...field}
              >
                {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((option, idx) => (
                  <MenuItem key={option} value={idx}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            name="dateOfOperation"
            control={control}
            render={({ field }) => (
              <DatePicker
                label="Date of Operation"
                sx={{ display: isReusable ? 'none' : 'block' }}
                format="DD/MM/YYYY"
                disablePast
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: field.value === null,
                  },
                }}
                {...field}
              />
            )}
          />
          <StyledFormActions>
            <Button text disabled={isLoading} onClick={onClose}>Cancel</Button>
            <Button primary disabled={isLoading} type="submit">Create</Button>
          </StyledFormActions>
        </StyledForm>
      </StyledContent>
    </Modal>
  )
}

const StyledForm = styled('form')`
  position: relative;
`

const StyledContent = styled('div')`
  ${(props) => props.theme.breakpoints.up('sm')} {
    min-width: ${(props) => props.theme.typography.pxToRem(540)};
  }
`

const StyledFormActions = styled('div')`
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  gap: ${(props) => props.theme.typography.pxToRem(12)};
  margin-top: ${(props) => props.theme.typography.pxToRem(16)};
`